import type { TOC } from '@ember/component/template-only';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';

interface GiveFeedbackBannerSignature {
  Args: {
    coachName: string;
    onClick: () => void;
  };
  Element: HTMLElement;
}

/**
 * Prompts the coachee to rate a coach once their partnership has concluded.
 *
 * Follows {@link AnnouncementBanner}'s theme-aware treatment — a card with a
 * `border-primary` outline and a `text-bg-primary` icon chip — both of which
 * the theme re-points at the lighter cascade accent (with dark text) in dark
 * mode, and body-coloured text that tracks the card surface in either theme.
 * Unlike the announcement banner it is not one big link: the call to action
 * is a real `btn-primary` that opens {@link GiveFeedbackModal} in place.
 */
const GiveFeedbackBanner: TOC<GiveFeedbackBannerSignature> = <template>
  {{#let (uniqueId) as |headingId|}}
    <section
      class="card border border-primary border-2 shadow-sm rounded-4 mb-4"
      aria-labelledby={{headingId}}
      data-test-give-feedback-banner
      ...attributes
    >
      <div
        class="card-body d-flex flex-column flex-sm-row align-items-sm-center gap-3 py-3 px-3 px-sm-4"
      >
        <span
          class="rounded-circle text-bg-primary d-none d-sm-flex align-items-center justify-content-center flex-shrink-0 p-3"
        >
          <iconify-icon inline icon="ph:star" class="fs-5" aria-hidden="true" />
        </span>
        <div class="flex-grow-1 min-w-0">
          <h2 id={{headingId}} class="h6 fw-bold mb-1 text-break">
            How was your coaching with
            {{@coachName}}?
          </h2>
          <p class="small text-body-secondary mb-0">
            Your coaching has come to an end, let us know how it went.
          </p>
        </div>
        <button
          type="button"
          class="btn btn-primary flex-shrink-0 align-self-start align-self-sm-center"
          data-test-give-feedback-button
          {{on "click" @onClick}}
        >
          Give feedback
        </button>
      </div>
    </section>
  {{/let}}
</template>;

export default GiveFeedbackBanner;
